import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp, Target, Briefcase, Sparkles, Coins, Trophy, Zap, ArrowRight,
  Users, MessageSquare, Award, FileText, Route as RouteIcon, Brain, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import Money from "@/components/Money";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import ActionCard from "@/components/ActionCard";
import EmptyState from "@/components/EmptyState";
import { StatCardSkeleton, CardSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { CommandCenterWidget } from "@/components/dashboard/widgets/CommandCenterWidget";
import { WelcomeOnboardingCard } from "@/components/dashboard/widgets/WelcomeOnboardingCard";
import { CareerHealthWidget } from "@/components/dashboard/widgets/CareerHealthWidget";
import { CareerScoreBreakdownWidget } from "@/components/dashboard/widgets/CareerScoreBreakdownWidget";
import { WhatsChangedWidget } from "@/components/dashboard/widgets/WhatsChangedWidget";
import { SalaryIntelligenceWidget } from "@/components/dashboard/widgets/SalaryIntelligenceWidget";
import { FinancialSnapshotWidget } from "@/components/dashboard/widgets/FinancialSnapshotWidget";

const pct = (n) => (n == null ? "—" : `${Math.round(n)}%`);

const ICON_MAP = {
  salary: TrendingUp, growth: TrendingUp, career: Target, resume: FileText,
  application: Briefcase, applications: Briefcase, network: Users, learning: Award,
  certification: Award, interview: MessageSquare, sparkles: Sparkles, ai: Brain,
};

export default function Dashboard() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setInsightsLoading(true);
    Promise.all([
      api.get("/dashboard/summary").then((r) => setSummary(r.data)),
      api.get("/insights").then((r) => setInsights(r.data)),
    ]).finally(() => setInsightsLoading(false));
  }, []);

  // Onboarding gate — decides whether the Welcome card or the Command Center
  // sits at the top of the dashboard. `null` = still loading (show neither).
  const [onboardingDone, setOnboardingDone] = useState(null);
  useEffect(() => {
    api
      .get("/dashboard/onboarding")
      .then((r) => setOnboardingDone(!!r.data?.onboarding_done))
      .catch(() => setOnboardingDone(false));
  }, []);

  const genInsights = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/insights/generate");
      setInsights(data);
      toast.success("Fresh AI insights generated");
    } catch (e) {
      toast.error("Failed to generate insights");
    } finally {
      setGenerating(false);
    }
  };

  const s = summary || {};
  const salaryGap =
    s.current_salary && s.market_median
      ? Math.round(((s.market_median - s.current_salary) / s.current_salary) * 100)
      : null;

  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Overview"
        title={`Welcome back, ${firstName}`}
        subtitle="Your Career OS at a glance. AI insights refresh daily. Cmd+K to jump anywhere."
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => nav("/app/chat")}
              data-testid="dashboard-ask-ai"
              className="gap-2"
            >
              <MessageSquare size={14} /> Ask AI
            </Button>
            <Button size="sm" onClick={genInsights} disabled={generating} data-testid="dashboard-refresh-insights" className="gap-2">
              <RefreshCw size={14} className={generating ? "animate-spin" : ""} />
              {generating ? "Generating…" : "Refresh Insights"}
            </Button>
          </>
        }
      />

      <DOBNudge summary={summary} />

      {/* ─────────────── Dashboard 2.0 — Welcome / Command Center ─────────────── */}
      <div className="mb-6" data-testid="dashboard-command-center">
        <AnimatePresence mode="wait" initial={false}>
          {onboardingDone === null ? null : onboardingDone ? (
            <motion.div
              key="cmd"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
            >
              <CommandCenterWidget />
            </motion.div>
          ) : (
            <WelcomeOnboardingCard
              key="welcome"
              onDone={() => setOnboardingDone(true)}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Row: Career Health + What's Changed */}
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 mb-6" data-testid="dashboard-health-row">
        <CareerHealthWidget />
        <WhatsChangedWidget size="lg" />
      </div>

      {/* Career Score Breakdown — full width */}
      <div className="mb-6" data-testid="dashboard-score-row">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
          <CareerScoreBreakdownWidget size="full" />
        </div>
      </div>

      {/* Row: Salary Intelligence + Financial Snapshot (Phase B) */}
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 mb-8" data-testid="dashboard-money-row">
        <SalaryIntelligenceWidget size="lg" />
        <FinancialSnapshotWidget size="lg" />
      </div>

      {/* ─────────────── Legacy KPIs / Insights / Funnel / XP ─────────────── */}
      <div className="flex items-center gap-3 mb-4 mt-2" data-testid="dashboard-details-heading">
        <div className="h-px flex-1 bg-border" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Details & activity
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Top row — 4 KPIs */}
      <section
        aria-label="Key metrics"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
        data-testid="dashboard-metrics"
      >
        {!summary ? (
          <>
            <StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              label="Current Salary"
              value={s.current_salary != null ? format(s.current_salary).replace(/[^\d,.\s\-A-Za-z]/g, (m) => m) : "—"}
              icon={Coins}
              helper={s.current_salary ? "Base compensation" : "Add your salary"}
              onClick={() => nav("/app/profile")}
              testId="metric-current-salary"
            />
            <StatCard
              label="Market Median"
              value={s.market_median != null ? format(s.market_median) : "—"}
              icon={TrendingUp}
              delta={salaryGap != null ? salaryGap : undefined}
              deltaLabel="%"
              helper={s.market_median ? "vs peers" : "Run salary intel"}
              onClick={() => nav("/app/salary")}
              testId="metric-market-median"
            />
            <StatCard
              label="Career Score"
              value={s.career_score ?? 0}
              suffix="/100"
              icon={Target}
              helper={s.career_score >= 80 ? "Excellent" : "Complete your profile"}
              onClick={() => nav("/app/profile")}
              testId="metric-career-score"
            />
            <StatCard
              label="Lifetime Earnings"
              value={s.lifetime_earnings_projection != null ? format(s.lifetime_earnings_projection, { compact: true, decimals: 1 }) : "—"}
              icon={Sparkles}
              helper="20-yr projection"
              onClick={() => nav("/app/salary")}
              testId="metric-lifetime"
            />
          </>
        )}
      </section>

      {/* Bento layout: main insights + right column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: AI insights */}
        <section aria-label="AI Insights" className="lg:col-span-2" data-testid="dashboard-insights">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-xl text-foreground flex items-center gap-2">
              <Brain size={18} strokeWidth={1.75} className="text-primary" />
              AI Insights
            </h2>
            {insights.length > 0 && (
              <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                Today · {insights.length}
              </span>
            )}
          </div>

          {insightsLoading ? (
            <div className="space-y-3">
              <CardSkeleton /><CardSkeleton />
            </div>
          ) : insights.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No insights yet"
              description="Generate personalized AI recommendations based on your profile and market data."
              actionLabel="Generate insights"
              onAction={genInsights}
              testId="dashboard-insights-empty"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {insights.slice(0, 4).map((it, i) => {
                const Icon = ICON_MAP[it.icon] || ICON_MAP[it.category] || Sparkles;
                return (
                  <motion.div
                    key={it.id || i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <ActionCard
                      title={it.title}
                      reason={it.description}
                      icon={Icon}
                      ctaLabel="Explore"
                      onAction={() => nav(insightRoute(it.category))}
                      testId={`insight-${i}`}
                    />
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>

        {/* Right: Application funnel + quick actions */}
        <aside className="space-y-6" aria-label="Application funnel and quick actions">
          <div className="bg-card border border-border rounded-xl p-5" data-testid="dashboard-funnel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-base text-foreground flex items-center gap-2">
                <Briefcase size={16} strokeWidth={1.75} />
                Application Funnel
              </h3>
              <Link to="/app/jobs" className="text-xs text-primary hover:underline font-medium">
                View all
              </Link>
            </div>
            {!summary ? (
              <div className="space-y-2"><div className="skeleton h-3 w-full" /><div className="skeleton h-3 w-2/3" /></div>
            ) : s.total_applications === 0 ? (
              <div className="text-center py-4">
                <div className="text-sm text-muted-foreground mb-3">No applications tracked yet.</div>
                <Button size="sm" onClick={() => nav("/app/jobs")} className="gap-2" data-testid="funnel-add-first">
                  Add first application <ArrowRight size={12} />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {[
                  { k: "applied", label: "Applied", tone: "bg-secondary" },
                  { k: "phone_screen", label: "Phone Screen", tone: "bg-primary/40" },
                  { k: "interview", label: "Interview", tone: "bg-primary/70" },
                  { k: "offer", label: "Offer", tone: "bg-[hsl(var(--success))]" },
                  { k: "accepted", label: "Accepted", tone: "bg-[hsl(var(--success))]" },
                  { k: "rejected", label: "Rejected", tone: "bg-destructive/40" },
                ].map((row) => {
                  const count = s.job_status_counts?.[row.k] || 0;
                  const total = s.total_applications || 1;
                  const w = Math.max(4, Math.round((count / total) * 100));
                  return (
                    <div key={row.k} className="text-xs">
                      <div className="flex items-center justify-between text-muted-foreground mb-1">
                        <span>{row.label}</span>
                        <span className="font-mono text-foreground">{count}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                        <motion.div
                          className={`h-full ${row.tone} rounded-full`}
                          initial={{ width: 0 }}
                          animate={{ width: `${w}%` }}
                          transition={{ duration: 0.5, delay: 0.05 }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Level widget */}
          <Link
            to="/app/achievements"
            className="block bg-card border border-border rounded-xl p-5 card-hover"
            data-testid="dashboard-level"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary text-primary-foreground flex flex-col items-center justify-center">
                <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">LVL</div>
                <div className="font-display font-black text-xl leading-none">{s.level ?? 1}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-base text-foreground flex items-center gap-1.5 flex-wrap">
                  <Trophy size={14} className="text-[hsl(var(--warning))]" />
                  {s.xp ?? 0} XP
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {s.badges_earned ?? 0}/{s.badges_total ?? 0} badges earned
                </div>
                <div className="h-1.5 rounded-full bg-secondary mt-2 overflow-hidden">
                  <motion.div
                    className="h-full bg-primary rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${s.progress_pct || 0}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
            </div>
          </Link>

          {/* Quick actions */}
          <div className="bg-card border border-border rounded-xl p-5" data-testid="dashboard-quick-actions">
            <h3 className="font-display font-bold text-base text-foreground mb-3 flex items-center gap-2">
              <Zap size={16} strokeWidth={1.75} />
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { to: "/app/resume", label: "Resume AI", icon: FileText },
                { to: "/app/roadmap", label: "Roadmap", icon: RouteIcon },
                { to: "/app/interview", label: "Interview", icon: MessageSquare },
                { to: "/app/network", label: "Network", icon: Users },
              ].map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className="flex items-center gap-2 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors text-sm font-medium text-foreground"
                  data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <a.icon size={14} className="text-muted-foreground" strokeWidth={1.75} />
                  {a.label}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function insightRoute(category) {
  const map = {
    salary: "/app/salary",
    career: "/app/roadmap",
    resume: "/app/resume",
    application: "/app/jobs",
    network: "/app/network",
    learning: "/app/certifications",
    certification: "/app/certifications",
    interview: "/app/interview",
  };
  return map[category] || "/app/chat";
}


/** DOB nudge — small, dismissable (session-only). Persistent per session
 *  but never blocks the UI. Shown until user completes their profile. */
function DOBNudge({ summary }) {
  const [dismissed, setDismissed] = useState(false);
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);
  if (dismissed || !profile) return null;
  if (profile.date_of_birth) return null;
  return (
    <div className="mb-6 rounded-xl border border-primary/30 bg-primary/6 p-3 flex items-center gap-3" data-testid="dob-nudge">
      <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
        <Sparkles size={14} />
      </div>
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-semibold">Personalize your AI advice.</span>{" "}
        <span className="text-muted-foreground">Add your date of birth so the AI understands your life stage and gives age-aware recommendations.</span>
      </div>
      <Link to="/app/profile" className="text-xs font-semibold text-primary whitespace-nowrap" data-testid="dob-nudge-cta">Complete profile →</Link>
      <button onClick={() => setDismissed(true)} className="text-xs text-muted-foreground hover:text-foreground px-2" aria-label="Dismiss" data-testid="dob-nudge-dismiss">×</button>
    </div>
  );
}
